import type { FormEvent, ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { API_ORIGIN } from '../api/client';
import {
  useAsana,
  useAsanaActions,
  useConnectedApps,
  useCreateMcpToken,
  useDisconnectApp,
  useDisconnectMySlack,
  useDisconnectSlack,
  useMcpTokens,
  useRevokeMcpToken,
  useSlack,
} from '../api/queries';
import {
  Broken,
  Button,
  Card,
  Checkbox,
  Chip,
  Empty,
  Field,
  Pill,
  Rows,
  Picker,
  SkeletonRows,
  Snippet,
  useNotices,
  Icon,
} from '../ui';
import { dateLabel, since } from '../lib/time';
import { copyText } from '../lib/note';
import { mcpClients } from '../settings/mcp-clients';
import { slackInstallUrl, slackMineUrl } from './SlackPost';
import { useUrlParam } from '../lib/url-tab';

/** Asana's own consent screen, when an app exists to send somebody to. */
export const asanaInstallUrl = `${API_ORIGIN}/v1/asana/start`;

const MCP_URL = `${API_ORIGIN || window.location.origin}/v1/mcp`;

const SCOPES: Array<[string, string]> = [
  ['notes:read', 'Meetings, notes, the ledger, people, series, spaces, search'],
  ['transcripts:read', 'Transcripts and the video link'],
  ['calendar:read', 'The week ahead, and notices'],
  ['ledger:write', 'Mark done or dropped, undo, name an owner'],
  ['folders:write', 'Create folders and file meetings'],
];

/** Connected apps — what a person has let into their meetings, and how they take it back; and tokens for the clients that cannot open a browser. */
export function ConnectedApps(): ReactElement {
  // ⚠ The token lives up here so a freshly made one lands in the snippets
  // below it. Otherwise setting up Codex means making a token, copying it,
  // scrolling back up and editing a placeholder out of a config block by hand
  // — which is exactly the kind of step people get wrong.
  const [secret, setSecret] = useState<string | null>(null);
  return (
    <>
      <SlackCard />
      <AsanaCard />
      <Setup token={secret} />
      <Card title="Connected apps" aside={<span className="mono">what you have let in</span>} flush>
        <Grants />
      </Card>
      <Tokens secret={secret} setSecret={setSecret} />
    </>
  );
}

/**
 * Slack, for the whole workspace (PRD 33).
 *
 * ⚠ One installation per workspace, made by an administrator, not a
 * per-person grant: the channel a team reads belongs to the team, and a
 * product where four people have each connected their own Slack posts the same
 * meeting four times.
 */
function SlackCard(): ReactElement {
  const slack = useSlack();
  const disconnect = useDisconnectSlack();
  const disconnectMine = useDisconnectMySlack();
  const { show } = useNotices();
  const [params, setParams] = useSearchParams();

  // Slack sends people back here with a word. Said once, then taken out of the
  // URL so a reload does not repeat it.
  const outcome = params.get('slack');
  useEffect(() => {
    if (!outcome) return;
    show(
      outcome === 'connected'
        ? { tone: 'success', text: 'Slack connected — a meeting’s ledger can be posted now.' }
        : outcome === 'refused'
          ? { tone: 'attention', text: 'Slack was not connected: an administrator has to do it.' }
          : { tone: 'failure', text: 'Slack did not finish connecting. Try again.' },
    );
    const next = new URLSearchParams(params);
    next.delete('slack');
    setParams(next, { replace: true });
  }, [outcome, params, setParams, show]);

  // …and from connecting somebody's OWN account (PRD 56 §6).
  const mineOutcome = params.get('slackme');
  useEffect(() => {
    if (!mineOutcome) return;
    show(
      mineOutcome === 'connected'
        ? {
            tone: 'success',
            text: 'Your Slack is connected — direct messages can come from you now.',
          }
        : { tone: 'failure', text: 'Your Slack did not finish connecting. Try again.' },
    );
    const next = new URLSearchParams(params);
    next.delete('slackme');
    setParams(next, { replace: true });
  }, [mineOutcome, params, setParams, show]);

  return (
    <Card title="Slack" aside={<span className="mono">the ledger, in a channel</span>} flush>
      <div className="stack" style={{ padding: '12px 20px', gap: 12 }}>
        {slack.isPending ? (
          <SkeletonRows rows={1} dense />
        ) : slack.isError ? (
          <Broken what="Slack" retry={() => void slack.refetch()} />
        ) : slack.data.connected ? (
          <div className="stack" style={{ gap: 10 }}>
            <div className="between">
              <span className="t-ui">
                Connected to <strong>{slack.data.teamName ?? 'a workspace'}</strong>
                {slack.data.installedAt ? (
                  <span className="mono"> · since {dateLabel(slack.data.installedAt)}</span>
                ) : null}
              </span>
              {slack.data.canManage ? (
                <div className="cluster" style={{ gap: 8 }}>
                  {/* ⚠ Reconnecting is how a new permission is granted: Slack
                      adds it to the installation, and nothing posted is lost.
                      Disconnecting first would drop the token for nothing. */}
                  <a className="btn btn-sm" href={slackInstallUrl}>
                    Reconnect
                  </a>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() =>
                      disconnect.mutate(undefined, {
                        onSuccess: () =>
                          show({
                            tone: 'success',
                            text: 'Slack disconnected — nothing more is posted.',
                          }),
                      })
                    }
                  >
                    Disconnect
                  </Button>
                </div>
              ) : null}
            </div>
            {/* ⚠ Anybody's, not only an administrator's: it grants Ptitim the
                right to send as THIS person, into their own DMs (PRD 56 §6). */}
            <div className="between">
              <span className="t-secondary">
                {slack.data.me.connected
                  ? 'Your Slack is connected — direct messages you send from a meeting come from you.'
                  : 'Your own Slack is only for direct messages from you — the app cannot write into a conversation between two people. Channels never need it.'}
              </span>
              {slack.data.me.connected ? (
                <Button
                  size="sm"
                  variant="quiet"
                  onClick={() =>
                    disconnectMine.mutate(undefined, {
                      onSuccess: () =>
                        show({ tone: 'success', text: 'Your Slack is disconnected.' }),
                    })
                  }
                >
                  Disconnect mine
                </Button>
              ) : (
                <a className="btn btn-sm" href={slackMineUrl}>
                  Connect my Slack
                </a>
              )}
            </div>
            {/* A workspace connected before direct messages were asked for
                (PRD 56) has to be connected again to allow them. */}
            {!slack.data.canSendDms ? (
              <span className="t-secondary">
                Sending actions to their owners as direct messages needs one more Slack permission.{' '}
                {slack.data.canManage
                  ? 'Press Reconnect and allow it — channel posts keep working either way.'
                  : 'An administrator grants it by reconnecting Slack here.'}
              </span>
            ) : null}
          </div>
        ) : (
          <div className="between">
            <span className="t-secondary">
              After a meeting, post what it produced to a channel: the commitments with the right
              people tagged, and a tick in Slack closes it here. Nothing is posted without somebody
              pressing post.
            </span>
            {slack.data.canManage ? (
              <a className="btn btn-primary btn-sm" href={slackInstallUrl}>
                Add to Slack
              </a>
            ) : (
              <span className="why">an administrator connects it</span>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * Asana, for one person (PRD 51).
 *
 * ⚠ **Per person, unlike Slack right above it.** A Slack installation belongs
 * to the workspace because a channel does; an Asana task belongs to whoever
 * created it. Vladimir: *"can we build it so that users will see task created
 * by them as they will connect asana account. Sort of how the asana connector
 * in Claude works"* — and he is not an admin of TraceAir's Asana, so a path
 * that needed an org-wide install would have left him unable to use it at all.
 *
 * A pasted personal access token is therefore the first-class way in, not a
 * fallback: it needs no app registration and no administrator. The OAuth
 * button appears only when an app actually exists to send somebody to.
 */
function AsanaCard(): ReactElement {
  const asana = useAsana();
  const { connect, disconnect } = useAsanaActions();
  const { show } = useNotices();
  const [params, setParams] = useSearchParams();
  const [token, setToken] = useState('');

  // Asana sends people back here with a word. Said once, then taken out of the
  // address so a reload does not say it again.
  const outcome = params.get('asana');
  useEffect(() => {
    if (!outcome) return;
    show(
      outcome === 'connected'
        ? { tone: 'success', text: 'Asana connected — a commitment can become a task now.' }
        : outcome === 'unavailable'
          ? {
              tone: 'attention',
              text: 'No Asana app is registered yet. Paste a personal access token instead.',
            }
          : { tone: 'failure', text: 'Asana did not finish connecting. Try again.' },
    );
    const next = new URLSearchParams(params);
    next.delete('asana');
    setParams(next, { replace: true });
  }, [outcome]);

  return (
    <Card title="Asana" aside={<span className="mono">a commitment, as a task</span>} flush>
      <div className="stack" style={{ padding: '12px 20px', gap: 12 }}>
        {asana.isPending ? (
          <SkeletonRows rows={1} dense />
        ) : asana.isError ? (
          <Broken what="Asana" retry={() => void asana.refetch()} />
        ) : asana.data.connected ? (
          <div className="between">
            <span className="t-ui">
              Connected as <strong>{asana.data.name ?? 'you'}</strong>
              <span className="mono"> · tasks are created as you</span>
            </span>
            <Button
              size="sm"
              variant="danger"
              onClick={() =>
                disconnect.mutate(undefined, {
                  onSuccess: () => show({ tone: 'success', text: 'Asana disconnected.' }),
                })
              }
            >
              Disconnect
            </Button>
          </div>
        ) : (
          <div className="stack" style={{ gap: 10 }}>
            <span className="t-secondary">
              Turn a commitment into an Asana task carrying the words that were actually said, the
              meeting in short, its owner as the assignee and its deadline. It connects to{' '}
              <strong>your own</strong> Asana, so the task is yours — no administrator and no
              workspace-wide install.
            </span>
            <div className="cluster" style={{ gap: 8, flexWrap: 'wrap' }}>
              <Field
                aria-label="Asana personal access token"
                placeholder="Paste a personal access token"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                style={{ width: 300 }}
              />
              <Button
                size="sm"
                variant="primary"
                disabled={token.trim().length < 8}
                working={connect.isPending ? 'Checking…' : false}
                onClick={() =>
                  connect.mutate(token.trim(), {
                    onSuccess: (data) => {
                      setToken('');
                      show({
                        tone: 'success',
                        text: `Connected as ${data.name ?? 'you'} — a commitment can become a task now.`,
                      });
                    },
                    onError: (error) => show({ tone: 'failure', text: error.message }),
                  })
                }
              >
                Connect
              </Button>
              {asana.data.oauthAvailable ? (
                <a className="btn btn-sm" href={asanaInstallUrl}>
                  or sign in with Asana
                </a>
              ) : null}
            </div>
            <span className="why">
              Make one at app.asana.com → your photo → My settings → Apps → Manage developer apps →
              Personal access tokens. It needs no admin.
            </span>
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * How to point each client at Ptitim.
 *
 * Vladimir, 2026-09-21: *"I want more instructions and maybe some commands to
 * copy … so users can just copy code or follow clear steps"*. The page used to
 * name the URL and stop, which is everything somebody who already knows MCP
 * needs and nothing at all for anybody else.
 */
function Setup({ token }: { token: string | null }): ReactElement {
  const [picked, setOn] = useUrlParam('client');
  const on = picked ?? 'claude';
  const clients = mcpClients(MCP_URL, token);
  const client = clients.find((c) => c.key === on) ?? clients[0];
  return (
    <Card title="Set it up" aside={<span className="mono">MCP · 27 tools</span>} flush>
      <div className="stack" style={{ padding: '12px 20px', gap: 14 }}>
        <span className="t-secondary">
          Ptitim is an MCP server: your meetings, notes, ledger and people, offered to an AI client
          as tools it can call. Pick yours.
        </span>
        <div className="cluster" role="tablist" aria-label="Client">
          {clients.map((c) => (
            <Chip key={c.key} on={c.key === client.key} onClick={() => setOn(c.key)}>
              {c.name}
            </Chip>
          ))}
        </div>
        <span className="t-secondary">
          {client.credential === 'connector'
            ? 'This one signs in for itself — no token to make, and you approve what it may read.'
            : client.credential === 'token'
              ? 'This one cannot open a browser, so it carries a personal token. Make one at the bottom of this page first.'
              : 'Either way: sign in through the browser, or carry a personal token where no browser can open.'}
        </span>
        <div className="stack" style={{ gap: 14 }}>
          {client.steps.map((step, i) => (
            <div key={step.text} className="setup-step">
              <span className="n">{i + 1}</span>
              <div className="stack" style={{ gap: 8 }}>
                <span className="t-ui">{step.text}</span>
                {step.code ? <Snippet code={step.code} where={step.where} /> : null}
              </div>
            </div>
          ))}
        </div>
        <span className="t-secondary">
          Whatever it connects as, it reads only what you can read, and only what you allowed. Every
          call is logged; nothing here can delete a meeting.
        </span>
      </div>
    </Card>
  );
}

function Grants(): ReactElement {
  const grants = useConnectedApps();
  const disconnect = useDisconnectApp();
  const { show } = useNotices();
  if (grants.isPending) return <SkeletonRows rows={1} dense />;
  if (grants.isError) return <Broken what="Connected apps" retry={() => void grants.refetch()} />;
  if (grants.data.length === 0)
    return (
      <Empty lead="Nothing connected yet.">
        A connector appears here the moment you approve it.
      </Empty>
    );
  return (
    <Rows>
      {grants.data.map((g) => (
        <div key={g.id} className="row">
          <div className="row-main">
            <span className="row-title">{g.name}</span>
            <span className="row-sub">
              <span className="mono">{g.scope.split(' ').join(' · ')}</span>
              <span className="mono">
                · since {dateLabel(g.createdAt)}
                {g.lastUsedAt ? ` · used ${since(g.lastUsedAt)}` : ' · never used'}
              </span>
            </span>
          </div>
          <Button
            size="sm"
            variant="danger"
            onClick={() =>
              disconnect.mutate(g.id, {
                onSuccess: () =>
                  show({
                    tone: 'success',
                    text: `${g.name} disconnected — its access ended at once.`,
                  }),
              })
            }
          >
            Disconnect
          </Button>
        </div>
      ))}
    </Rows>
  );
}

function Tokens({
  secret,
  setSecret,
}: {
  secret: string | null;
  setSecret: (token: string | null) => void;
}): ReactElement {
  const tokens = useMcpTokens();
  const create = useCreateMcpToken();
  const revoke = useRevokeMcpToken();
  const { show } = useNotices();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['notes:read', 'transcripts:read']);
  const [days, setDays] = useState('');
  const toggle = (scope: string, on: boolean) =>
    setScopes((s) => (on ? [...new Set([...s, scope])] : s.filter((x) => x !== scope)));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      show({
        tone: 'attention',
        text: 'Give the token a name — what will use it — so you know which one to revoke later.',
      });
      return;
    }
    if (scopes.length === 0) {
      show({ tone: 'attention', text: 'Tick at least one scope — what the token may read or do.' });
      return;
    }
    create.mutate(
      { name: name.trim(), scopes, expiresInDays: days ? Number(days) : null },
      {
        onSuccess: (t) => {
          setSecret(t.token);
          setName('');
        },
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );
  };
  const live = (tokens.data ?? []).filter((t) => !t.revokedAt);
  return (
    <Card
      title="Personal tokens"
      aside={<span className="mono">for a shell, a script, a headless client</span>}
      flush
    >
      <div style={{ padding: '12px 20px' }} className="t-secondary stack">
        <span>
          A token opens the MCP door and nothing else — there are no endpoints behind it, there are
          tools, the same twenty-seven a connected app gets. It is for a client that cannot approve
          in a browser: a shell, a script, a headless MCP client. Send it as{' '}
          <code className="mono">Authorization: Bearer ptitim_mcp_…</code> to{' '}
          <code className="mono">{MCP_URL}</code>. Shown once, at creation; it carries the scopes
          you tick and nothing more.
        </span>
        <span className="mono" style={{ whiteSpace: 'normal' }}>
          notes:read → get_account_info · list_meetings · search_meetings · get_meeting ·
          get_open_ledger · list_people · get_person · list_series · get_series · list_spaces ·
          get_space · list_folders · list_knowledge · search_knowledge · ask
          <br />
          transcripts:read → get_transcript · get_video_link · search_transcript · who_talks_about
          <br />
          calendar:read → list_upcoming_meetings · list_notices
          <br />
          ledger:write → close_commitment · reopen_commitment · assign_owner
          <br />
          folders:write → create_folder · file_meeting · unfile_meeting
        </span>
      </div>
      {secret ? (
        <div className="notice notice-success" style={{ margin: '0 20px 12px' }}>
          <span className="notice-glyph">
            <Icon name="check" size={14} />
          </span>
          <span className="grow" style={{ wordBreak: 'break-all' }}>
            <span className="mono" style={{ color: 'var(--ink)', userSelect: 'all' }}>
              {secret}
            </span>
            <br />
            <span className="t-secondary">
              Copy it now — it will not be shown again. It is already in the commands at the top of
              this page.
            </span>
          </span>
          <Button
            size="sm"
            onClick={() =>
              void copyText(secret).then(() => show({ tone: 'success', text: 'Token copied.' }))
            }
          >
            Copy
          </Button>
          <Button size="sm" variant="quiet" onClick={() => setSecret(null)}>
            Done
          </Button>
        </div>
      ) : null}
      {tokens.isPending ? (
        <SkeletonRows rows={1} dense />
      ) : tokens.isError ? (
        <Broken what="Tokens" retry={() => void tokens.refetch()} />
      ) : live.length === 0 ? null : (
        <Rows>
          {live.map((t) => (
            <div key={t.id} className="row dense">
              <div className="row-main">
                <span className="row-title">
                  {t.name} <span className="mono">{t.prefix}…</span>
                </span>
                <span className="row-sub">
                  <span className="mono">{t.scopes.join(' · ')}</span>
                  <span className="mono">
                    · {t.lastUsedAt ? `used ${since(t.lastUsedAt)}` : 'never used'}
                    {t.expiresAt ? ` · expires ${dateLabel(t.expiresAt)}` : ''}
                  </span>
                </span>
              </div>
              <Button
                size="sm"
                variant="danger"
                onClick={() =>
                  revoke.mutate(t.id, {
                    onSuccess: () => show({ tone: 'success', text: `“${t.name}” revoked.` }),
                  })
                }
              >
                Revoke
              </Button>
            </div>
          ))}
        </Rows>
      )}
      <form
        onSubmit={submit}
        className="stack"
        style={{ padding: '12px 20px', borderTop: '1px solid var(--line)' }}
      >
        <div className="cluster" style={{ flexWrap: 'nowrap', alignItems: 'flex-end' }}>
          <Field
            label="New token"
            placeholder="What will use it — “Claude Code on the laptop”"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Picker
            label="Expires"
            value={days}
            onChange={setDays}
            width={150}
            options={[
              { value: '', label: 'never' },
              { value: '30', label: 'in 30 days' },
              { value: '90', label: 'in 90 days' },
              { value: '365', label: 'in a year' },
            ]}
          />
        </div>
        <div className="cluster">
          {SCOPES.map(([scope, label]) => (
            <Checkbox
              key={scope}
              label={`${scope} — ${label}`}
              checked={scopes.includes(scope)}
              onChange={(e) => toggle(scope, e.target.checked)}
            />
          ))}
        </div>
        <div>
          <Button type="submit" variant="primary" working={create.isPending ? 'Creating…' : false}>
            Create the token
          </Button>
          {scopes.length === 0 ? <Pill tone="amber">tick at least one scope</Pill> : null}
        </div>
      </form>
    </Card>
  );
}
